import React, { createContext, useState, useContext, useCallback, useEffect } from 'react';
import { de } from '../config/i18n/de';

export const LanguageContext = createContext();

// DeepL Free API — uses api-free.deepl.com endpoint (:fx key suffix)
const DEEPL_ENDPOINT = 'https://api-free.deepl.com/v2/translate';
const DEEPL_KEY = import.meta.env.VITE_DEEPL_API_KEY;

// 1 EUR ≈ 91 INR (update periodically)
const EUR_RATE = 91;

// Lightweight hash for cache keys
function hashText(str) {
  let h = 0;
  for (let i = 0; i < str.length; i++) {
    h = Math.imul(31, h) + str.charCodeAt(i) | 0;
  }
  return 'kh_tr_' + Math.abs(h).toString(36);
}

export const LanguageProvider = ({ children }) => {
  // Auto-detect .de domain, fallback to localStorage, then 'en'
  const isDeDomain =
    typeof window !== 'undefined' &&
    (window.location.hostname.endsWith('.de') ||
      window.location.hostname === 'kaaleenhaus.de' ||
      window.location.hostname === 'www.kaaleenhaus.de');

  const savedLang =
    typeof window !== 'undefined' ? localStorage.getItem('kh_lang') : null;
  const initialLang = savedLang || (isDeDomain ? 'de' : 'en');

  const [lang, setLangState] = useState(initialLang);

  // Set html lang attribute on mount and on change
  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  const setLang = useCallback((newLang) => {
    setLangState(newLang);
    localStorage.setItem('kh_lang', newLang);
    document.documentElement.lang = newLang;
  }, []);

  // ── t(key, englishFallback) ─────────────────────────────
  // Resolves dot-notation key from de.js in German mode.
  // In English mode, returns the fallback string.
  const t = useCallback(
    (key, fallback = '') => {
      if (lang !== 'de') return fallback || '';
      const parts = key.split('.');
      let val = de;
      for (const part of parts) {
        val = val?.[part];
        if (val === undefined) return fallback || '';
      }
      if (typeof val === 'string' || typeof val === 'function') return val;
      return fallback || '';
    },
    [lang]
  );

  // ── translateText(text) ──────────────────────────────────
  // Translates a single string via DeepL, with localStorage cache.
  const translateText = useCallback(
    async (text) => {
      if (!text || !text.trim() || lang !== 'de') return text;
      const cacheKey = hashText(text.trim());
      const cached = localStorage.getItem(cacheKey);
      if (cached) return cached;
      if (!DEEPL_KEY) return text;
      try {
        const res = await fetch(DEEPL_ENDPOINT, {
          method: 'POST',
          headers: {
            Authorization: `DeepL-Auth-Key ${DEEPL_KEY}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            text: [text],
            target_lang: 'DE',
            source_lang: 'EN',
          }),
        });
        const data = await res.json();
        const translated = data.translations?.[0]?.text || text;
        try { localStorage.setItem(cacheKey, translated); } catch (_) {}
        return translated;
      } catch {
        return text;
      }
    },
    [lang]
  );

  // ── translateBatch(textsArray) ───────────────────────────
  // Batches multiple strings into one DeepL API call.
  // Cache-checks each individually first.
  const translateBatch = useCallback(
    async (texts) => {
      if (!texts || !texts.length || lang !== 'de') return texts;
      const results = [...texts];
      const toFetch = [];
      const indices = [];

      texts.forEach((text, i) => {
        if (!text || !text.trim()) return;
        const cached = localStorage.getItem(hashText(text.trim()));
        if (cached) { results[i] = cached; }
        else { toFetch.push(text); indices.push(i); }
      });

      if (!toFetch.length || !DEEPL_KEY) return results;

      try {
        const res = await fetch(DEEPL_ENDPOINT, {
          method: 'POST',
          headers: {
            Authorization: `DeepL-Auth-Key ${DEEPL_KEY}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            text: toFetch,
            target_lang: 'DE',
            source_lang: 'EN',
          }),
        });
        const data = await res.json();
        (data.translations || []).forEach((tr, idx) => {
          const orig = toFetch[idx];
          const translated = tr.text || orig;
          results[indices[idx]] = translated;
          try { localStorage.setItem(hashText(orig.trim()), translated); } catch (_) {}
        });
      } catch (_) {}

      return results;
    },
    [lang]
  );

  // ── formatPrice(inrAmount) ───────────────────────────────
  // Returns price object. In German mode: shows € primary + ₹ secondary.
  const formatPrice = useCallback(
    (inrAmount) => {
      const inr = Number(inrAmount);
      if (!Number.isFinite(inr) || inr <= 0) return { display: '', inr: '', eur: null };
      const inrStr = `₹${inr.toLocaleString('en-IN')}`;
      if (lang !== 'de') return { display: inrStr, inr: inrStr, eur: null };
      const eur = Math.round(inr / EUR_RATE);
      const eurStr = `€${eur.toLocaleString('de-DE')}`;
      return { display: eurStr, inr: inrStr, eur: eurStr };
    },
    [lang]
  );

  // ── filterLabel(category, value) ─────────────────────────
  // Returns translated display label for filter option values.
  // The English value is still used for API queries.
  const filterLabel = useCallback(
    (category, value) => {
      if (lang !== 'de') return value;
      return de.shop.filterLabels?.[category]?.[value] || value;
    },
    [lang]
  );

  return (
    <LanguageContext.Provider
      value={{ lang, setLang, t, translateText, translateBatch, formatPrice, filterLabel }}
    >
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => useContext(LanguageContext);

// ── useDynamicTranslation(text) hook ────────────────────────
// Convenience hook: returns translated text (async).
// Shows original while loading — no blank flicker.
export function useDynamicTranslation(text) {
  const { lang, translateText } = useLanguage();
  const [translated, setTranslated] = useState(text || '');

  useEffect(() => {
    let cancelled = false;
    if (!text) { setTranslated(''); return; }
    if (lang !== 'de') { setTranslated(text); return; }
    translateText(text).then((result) => {
      if (!cancelled) setTranslated(result);
    });
    return () => { cancelled = true; };
  }, [text, lang, translateText]);

  return translated;
}

// ── PriceTag component ───────────────────────────────────────
// Drop-in replacement for price display across the site.
export function PriceTag({ inr, className, subStyle }) {
  const { lang, formatPrice } = useLanguage();
  const p = formatPrice(inr);
  if (!p.display) return null;
  if (lang !== 'de') return <span className={className}>{p.display}</span>;
  return (
    <span className={className}>
      {p.eur}
      <span style={{ fontSize: '0.75em', color: '#888', marginLeft: '5px', fontWeight: 400, ...subStyle }}>
        ({p.inr})
      </span>
    </span>
  );
}
